// Admin Analytics Tab Component
import React from 'react';
import { GroupBox } from '../shared/GroupBox';
import { CreditCard, Calendar, TrendingUp, Activity } from 'lucide-react';
import { useAdmin } from './AdminContext';

export function AdminAnalyticsTab() {
  const { analytics } = useAdmin();

  return (
    <div className="space-y-4">
      {/* Subscription & Conversion Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border border-green-500/30 bg-green-900/10">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-400">Active Subscriptions</span>
          </div>
          <div className="text-2xl font-bold text-green-500">{analytics?.subscription_metrics?.active || 0}</div>
        </div>
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-900/10">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-gray-400">Trial Users</span>
          </div>
          <div className="text-2xl font-bold text-amber-500">{analytics?.subscription_metrics?.trial || 0}</div>
        </div>
        <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-900/10">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-400">Conversion Rate</span>
          </div>
          <div className="text-2xl font-bold text-blue-500">{analytics?.conversion?.rate || 0}%</div>
        </div>
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-900/10">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-red-500" />
            <span className="text-xs text-gray-400">Churned (30d)</span>
          </div>
          <div className="text-2xl font-bold text-red-500">{analytics?.churn?.last_30_days || 0}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Growth */}
        <GroupBox title="User Growth (12 months)" className="border-blue-500/50">
          <div className="p-4 h-64">
            <div className="flex items-end justify-between h-full gap-2">
              {analytics?.user_growth?.map((item, idx) => {
                const maxCount = Math.max(...(analytics.user_growth || []).map(i => i.count || 1), 1);
                const height = Math.max(10, ((item.count || 0) / maxCount) * 150);
                return (
                  <div key={idx} className="flex flex-col items-center flex-1 group">
                    <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 mb-1">{item.count || 0}</div>
                    <div 
                      className="w-full bg-blue-500/50 rounded-t hover:bg-blue-500/70 transition-colors"
                      style={{ height: `${height}px` }}
                    />
                    <span className="text-[10px] text-gray-500 mt-1">{(item.month || '').split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </GroupBox>

        {/* Revenue */}
        <GroupBox title="Revenue (12 months)" className="border-green-500/50">
          <div className="p-4 h-64">
            <div className="flex items-end justify-between h-full gap-2">
              {analytics?.revenue_data?.map((item, idx) => {
                const maxRevenue = Math.max(...(analytics.revenue_data || []).map(i => i.revenue || 1), 1);
                const height = Math.max(10, ((item.revenue || 0) / maxRevenue) * 150);
                return (
                  <div key={idx} className="flex flex-col items-center flex-1 group">
                    <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 mb-1">₹{(item.revenue || 0).toLocaleString()}</div>
                    <div 
                      className="w-full bg-green-500/50 rounded-t hover:bg-green-500/70 transition-colors"
                      style={{ height: `${height}px` }}
                    />
                    <span className="text-[10px] text-gray-500 mt-1">{(item.month || '').split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </GroupBox>
      </div>

      {/* Conversion Funnel */}
      <GroupBox title="Conversion Funnel" className="border-purple-500/50">
        <div className="p-4">
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-500">{analytics?.conversion?.total_signups || 0}</div>
              <div className="text-sm text-gray-400">Total Signups</div>
            </div>
            <div className="text-2xl text-gray-600">→</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-500">{analytics?.conversion?.paid_users || 0}</div>
              <div className="text-sm text-gray-400">Paid Users</div>
            </div>
            <div className="text-2xl text-gray-600">=</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">{analytics?.conversion?.rate || 0}%</div>
              <div className="text-sm text-gray-400">Conversion Rate</div>
            </div>
          </div>
        </div>
      </GroupBox>

      {/* Top Tenants */}
      <GroupBox title="Top Active Tenants (by transactions)" className="border-yellow-500/50">
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-2">#</th>
                <th className="text-left py-2 px-2">Tenant</th>
                <th className="text-center py-2 px-2">Plan</th>
                <th className="text-right py-2 px-2">Transactions</th>
                <th className="text-right py-2 px-2">Clients</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.top_tenants?.map((tenant, idx) => (
                <tr key={idx} className="border-b border-gray-800 last:border-0">
                  <td className="py-2 px-2 text-yellow-500 font-bold">#{idx + 1}</td>
                  <td className="py-2 px-2">{tenant.name}</td>
                  <td className="py-2 px-2 text-center">
                    <span className="px-2 py-0.5 rounded text-xs bg-gray-700">{tenant.plan}</span>
                  </td>
                  <td className="py-2 px-2 text-right text-blue-400">{tenant.transactions}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{tenant.clients}</td>
                </tr>
              ))}
              {(!analytics?.top_tenants || analytics.top_tenants.length === 0) && (
                <tr>
                  <td colSpan="5" className="py-4 text-center text-gray-500">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GroupBox>
    </div>
  );
}
